<template>
  <BaseSlideOverModal
    v-model:is-modal-open="localOpen"
    width="2xl"
    :title="$t('attributes.address')"
  >
    <template #cta>
      <div class="flex h-full gap-x-1">
        <template v-if="internalTab === 'list'">
          <BaseButton
            :title="$t('button.add_address')"
            icon="plus"
            size="small"
            @click="handleCreateAddressButton"
          />
        </template>
        <template v-else>
          <BaseButton
            :title="$t('button.back')"
            color="dark"
            size="small"
            icon="long-arrow-left"
            @click="resumeToList"
          />
          <BaseButton
            v-if="address"
            :title="formButtonTitle"
            icon="paper-plane"
            size="small"
            :disabled="!isValidate() || isSubmitting"
            color="primary"
            @click="submitForm"
          />
        </template>
      </div>
    </template>
    <template #body>
      <div
        class="-m-1 flex h-full w-full flex-col justify-between overflow-auto p-1"
      >
        <AddressModalList
          v-if="internalTab === 'list'"
          v-model="internalValue"
          :wiki-address="wikiAddress"
          @edit-address="handleEditAddress"
        />
        <AddressModalForm
          v-else
          :selected-address-id="selectedAddressId"
          :wiki-address="wikiAddress"
          :place-city-id="placeCityId"
        />
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal'
import AddressModalList from '@ui/modal/address/components/AddressModalList'
import AddressModalForm from '@ui/modal/address/components/AddressModalForm'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'

export default {
  name: 'AddressModal',
  components: {
    BaseButton,
    AddressModalForm,
    AddressModalList,
    BaseSlideOverModal
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    modelValue: {
      type: [Object, String, Number],
      required: false,
      default: null
    },
    fullModelResponse: {
      type: Boolean,
      required: false,
      default: false
    },
    wikiAddress: {
      type: Boolean,
      required: false,
      default: false
    },
    placeCityId: {
      type: [String, Number],
      required: false,
      default: null
    }
  },
  emits: ['close', 'update:modelValue', 'change', 'update:open'],
  data() {
    return {
      internalTab: 'list',
      selectedAddressId: null,
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters({
      address: 'address/getAddress'
    }),
    localOpen: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    formButtonTitle() {
      if (!this.address) return this.$t('button.save')
      return this.address.id
        ? this.$t('button.update_address')
        : this.$t('button.save_address')
    },
    internalValue: {
      get() {
        return this.modelValue
      },
      set(newValue) {
        this.$emit(
          'update:modelValue',
          this.fullModelResponse ? newValue : newValue.id
        )
        this.$emit('change', newValue)
        this.localOpen = false
      }
    }
  },
  watch: {
    open: {
      handler(newValue) {
        if (newValue) {
          if (!this.wikiAddress) {
            this.processRefreshSpecificInitialMetadataModelsAction({
              model: 'addresses'
            })
          } else {
            this.processFetchWikiAddressesByCityAction({
              city_id: this.$route.params.city_id ?? this.placeCityId
            })
          }
          this.internalTab = 'list'
        }
      }
    },
    immediate: true,
    deep: true
  },
  methods: {
    ...mapActions({
      processStoreNewAddressAction: 'address/storeNewAddress',
      processStoreWikiAddressAction: 'wiki/storeWikiAddress',
      processUpdateAddressAction: 'address/updateAddress',
      processUpdateWikiAddressAction: 'wiki/updateWikiAddress',
      processRefreshSpecificInitialMetadataModelsAction:
        'metadata/refreshSpecificInitialMetadataModels',
      processFetchAllMetadatasAction: 'metadata/fetchAllMetadata',
      processFetchWikiAddressesByCityAction: 'wiki/fetchWikiAddressesByCity'
    }),
    async submitForm() {
      if (this.isSubmitting) return

      if (!this.isValidate()) return

      this.isSubmitting = true
      try {
        if (!this.wikiAddress) {
          if (this.selectedAddressId) {
            await this.processUpdateAddressAction({
              id: this.selectedAddressId,
              address: this.address,
              successCallback: () => {
                this.$h.toastify(this.$t('toasts.updated_address'), {
                  className: 'toastify-content success'
                })
                this.processRefreshSpecificInitialMetadataModelsAction({
                  model: 'addresses'
                })
                this.internalTab = 'list'
              }
            })
          } else {
            await this.processStoreNewAddressAction({
              address: this.address,
              successCallback: async () => {
                this.$h.toastify(this.$t('toasts.created_address'), {
                  className: 'toastify-content success'
                })
                await this.processRefreshSpecificInitialMetadataModelsAction({
                  model: 'addresses'
                })
                this.internalTab = 'list'
              }
            })
          }
        } else {
          if (this.selectedAddressId) {
            await this.processUpdateWikiAddressAction({
              address_id: this.selectedAddressId,
              address: {
                ...this.address,
                place_city_id: this.placeCityId
              }
            }).then((response) => {
              this.$h.toastSuccess(this.$t('toasts.updated_address'))
              this.processFetchWikiAddressesByCityAction({
                city_id: this.$route.params.city_id ?? this.placeCityId
              })
              this.internalTab = 'list'
            })
          } else {
            await this.processStoreWikiAddressAction({
              address: {
                ...this.address,
                place_city_id: this.placeCityId
              }
            }).then((response) => {
              this.$h.toastSuccess(this.$t('toasts.created_address'))
              this.processFetchWikiAddressesByCityAction({
                city_id: this.$route.params.city_id ?? this.placeCityId
              })
              this.internalTab = 'list'
            })
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.isSubmitting = false
      }
    },
    isValidate() {
      return (
        !!this.address?.name &&
        !!this.address?.street &&
        !!this.address?.city &&
        !!this.address?.zip &&
        !!this.address?.country &&
        !!this.address?.latitude &&
        !!this.address?.longitude
      )
    },
    resumeToList() {
      this.internalTab = 'list'
    },
    handleEditAddress(address_id) {
      this.selectedAddressId = address_id
      this.internalTab = 'form'
    },
    handleCreateAddressButton() {
      this.selectedAddressId = null
      this.internalTab = 'form'
    }
  }
}
</script>
